/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import moment from 'moment';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AggregationInterval, RlFlLinkUsage } from '../../../shared/entities/dataUsage';
import { selectCumulativeUsageByLink } from '../../../store';
import { requestUsageByLinkCumulative } from '../../../store/dataUsage/actions';
import { PlaceholderHeading, PlaceholderText, PlaceholderWrapper } from '../DataUsageChartsCommonComponents';
import DataUsageChartContainer from './DataUsageChartContainer';
import UsageLineChart from './UsageLineChart';

function translateChartData(data: { [sourceTime: number]: RlFlLinkUsage }, key: keyof RlFlLinkUsage) {
  return Object.keys(data)
    .sort()
    .map(timestamp => ({
      x: moment.unix(parseInt(timestamp)).toDate(),
      y: data[timestamp][key],
    }));
}

function getSeriesData(
  data: { [sourceTime: number]: RlFlLinkUsage } | undefined,
  key: keyof RlFlLinkUsage,
  name: string,
  shortenedName: string,
) {
  return {
    name,
    data: data ? translateChartData(data, key) : [],
    tooltipShortenedName: shortenedName,
  };
}

interface CumulativeLinkUsageChartProps {
  width: number;
}
function CumulativeLinkUsageChart({ width }: CumulativeLinkUsageChartProps) {
  const dispatch = useDispatch();
  const cumulativeUsageByLink = useSelector(selectCumulativeUsageByLink, shallowEqual);

  const series = [
    getSeriesData(cumulativeUsageByLink?.usageByLinkBytes, 'rlUsageBytes', 'Return Link', 'Return Link'),
    getSeriesData(cumulativeUsageByLink?.usageByLinkBytes, 'flUsageBytes', 'Forward Link', 'Forward Link'),
  ];

  return (
    <DataUsageChartContainer
      width={width}
      header="Link Usage (Cumulative)"
      aggregationInterval={cumulativeUsageByLink?.aggregationInterval}
      menuItemClickHandler={(aggregationInterval: AggregationInterval) => {
        if (cumulativeUsageByLink) {
          dispatch(
            requestUsageByLinkCumulative({
              fromMsSinceEpoch: cumulativeUsageByLink.fromMsSinceEpoch,
              toMsSinceEpoch: cumulativeUsageByLink.toMsSinceEpoch,
              datapathUuids: cumulativeUsageByLink.datapathUuids,
              aggregationInterval: aggregationInterval,
            }),
          );
        }
      }}
    >
      <UsageLineChart
        series={series}
        placeholder={
          <PlaceholderWrapper>
            <PlaceholderHeading>No Terminals</PlaceholderHeading>
            <PlaceholderText>To view data usage analytics, select one or multiple terminals</PlaceholderText>
          </PlaceholderWrapper>
        }
        showPlaceholder={cumulativeUsageByLink === undefined}
        aggregationInterval={cumulativeUsageByLink?.aggregationInterval}
        userEndDateMs={cumulativeUsageByLink?.toMsSinceEpoch}
        width={width}
      />
    </DataUsageChartContainer>
  );
}

export default CumulativeLinkUsageChart;
